/**
 * WordPress dependencies.
 */
import api from "@wordpress/api";
import { __ } from "@wordpress/i18n";
import { useEffect, useState } from "@wordpress/element";

/**
 * Notices
 */
import { useDispatch } from "@wordpress/data";
import { store as noticesStore } from "@wordpress/notices";

/**
 * useSettings Hook.
 *
 * useSettings hook to get/update WordPress' settings database.
 *
 * Setting field needs to be registered to REST for this function to work.
 *
 * This hook works similar to get_option and update_option in PHP just without the option for a default value.
 * For notificiations to work, you need to add a Snackbar section to your React codebase if it isn't being
 * used inside the block editor.
 *
 * @see https://github.com/WordPress/gutenberg/blob/trunk/packages/editor/src/components/editor-snackbars/index.js
 *
 */
const useSettings = () => {
	const { createNotice } = useDispatch(noticesStore);
	const { createErrorNotice } = useDispatch(noticesStore);
	const { createSuccessNotice } = useDispatch(noticesStore);

	const [settings, setSettings] = useState({});
	const [status, setStatus] = useState("loading");
	const [isAtive, SetIfAtive] = useState(false);

	const getSettings = () => {
		api.loadPromise.then(async () => {
			try {
				const settings = new api.models.Settings();
				const response = await settings.fetch();
				setSettings(response);
				SetIfAtive(Boolean(response?.["aruba_hispeed_cache_options"].ahsc_enable_purge));
			} catch (error) {
				setStatus("error");
			} finally {
				setStatus("loaded");
			}
		});
	};

	useEffect(() => {
		getSettings();
	}, []);

	const getOption = () => {
		return settings?.["aruba_hispeed_cache_options"];
	};

	const updateOption = (option, target, value, success = __("Settings saved.", "aruba-hispeed-cache")) => {
		setStatus("saving");

		option[target] = value;

		const save = new api.models.Settings({ ["aruba_hispeed_cache_options"]: option }).save();

		save.success((response, status) => {
			if ("success" === status) {
				setStatus("loaded");

				if (target === "ahsc_enable_purge") {
					SetIfAtive(option.ahsc_enable_purge);
					if (false === option.ahsc_enable_purge) {
						createErrorNotice(
							__(
								"You have correctly disabled Aruba HiSpeed Cache Plugin.",
								"aruba-hispeed-cache"
							),
							{ isDismissible: true }
						);
					}

					if (true === option.ahsc_enable_purge) {
						createSuccessNotice(
							__("The clearing function of the Aruba HiSpeed Cache plugin has been correctly activated.", "aruba-hispeed-cache", "aruba-hispeed-cache"),
							{ isDismissible: true }
						);
					}
				} else {
					createNotice("success", success, {
						isDismissible: true,
						type: "snackbar",
					});
				}
			}

			if ("error" === status) {
				setStatus("error");

				createNotice("error", __("An error occurred, please try again later.", "aruba-hispeed-cache"), {
					isDismissible: true,
					type: "snackbar",
				});
			}

			getSettings();
		});

		save.error((response) => {
			setStatus("error");

			createNotice(
				"error",
				response.responseJSON.message
					? response.responseJSON.message
					: __("An error occurred, please try again later.", "aruba-hispeed-cache"),
				{
					isDismissible: true,
					type: "snackbar",
				}
			);
		});
	};

	return [getOption, updateOption, status, isAtive];
};

export default useSettings;
